import { useState, useMemo } from 'react';
import { LtButton, LtModal, DynamicMultiselector, ExpandableTable, LtIcon, IndicatorModal } from 'limetray-ui-kit';
import { userManagerConstants } from '../../../../constants/userManagerConstants';
import LtLoader from '../../../../components/LtLoader';

const { createUserModal } = userManagerConstants;

/**
 * UserBrandAccessSection
 *
 * Renders the "Brand Access" section for user create/edit.
 * Uses the limetray-ui-kit's DynamicMultiselector to select multiple outlets
 * from multiple brands.
 */
function UserBrandAccessSection({
  selectedBrandOutlets, // format: { [brandId]: [outletId1, outletId2] }
  onChange,
  mode,
  brands = [],
  setBrands,
  fetchBrands,
  brandsLoading,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  // We map the incoming object to a flat array for the multiselector
  const selectedOutletIds = useMemo(() => {
    let ids = [];
    if (selectedBrandOutlets) {
      const hasGlobalAll = selectedBrandOutlets["ALL"]?.includes("ALL");
      
      brands.forEach(brand => {
        const brandId = brand.brandId;
        const brandOutlets = selectedBrandOutlets[brandId] || [];
        const isAllSelected = hasGlobalAll || brandOutlets.includes("ALL");
        
        if (isAllSelected) {
          // If "ALL" is selected, add all outlet IDs of this brand
          const allOutletIdsForBrand = brand.outletData.map(o => String(o.outletId));
          ids = [...ids, ...allOutletIdsForBrand];
        } else {
          // Otherwise, add only the individually selected outlet IDs
          ids = [...ids, ...brandOutlets.map(String)];
        }
      });
    }
    return Array.from(new Set(ids));
  }, [selectedBrandOutlets, brands]);

  const handleOpenModal = async () => {
    setIsModalOpen(true);
    if (brands.length === 0 && !brandsLoading && !loading) {
      setLoading(true);
      try {
        await fetchBrands();
      } catch (error) {
        console.error("Failed to fetch brands and outlets", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveSelection = (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) {
      setIsErrorModalOpen(true);
      return;
    }
    const newSelection = {};
    
    // Group selected IDs by brand
    brands.forEach(b => {
      const brandId = b.brandId;
      const allBrandOutletIds = b.outletData.map(o => String(o.outletId));
      
      // Find which of these brand outlets are in selectedIds
      const selectedForThisBrand = allBrandOutletIds.filter(id => selectedIds.includes(id));
      
      if (selectedForThisBrand.length > 0) {
        // If all outlets of this brand are selected, we represent it as ["ALL"]
        if (selectedForThisBrand.length === allBrandOutletIds.length) {
          newSelection[brandId] = ["ALL"];
        } else {
          newSelection[brandId] = selectedForThisBrand;
        }
      }
    });

    const totalBrands = brands.length;
    let allBrandsSelectedAll = totalBrands > 0;
    brands.forEach(b => {
      if (!newSelection[b.brandId] || !newSelection[b.brandId].includes("ALL")) {
        allBrandsSelectedAll = false;
      }
    });
    
    if (allBrandsSelectedAll) {
      onChange({ "ALL": ["ALL"] });
    } else {
      onChange(newSelection);
    }
    
    setIsModalOpen(false);
  };

  const hasSelected = selectedBrandOutlets && Object.keys(selectedBrandOutlets).length > 0;
  const buttonLabel = (mode === 'edit' && hasSelected)
    ? createUserModal.sectionTitles.brandAccess.editButton
    : createUserModal.sectionTitles.brandAccess.button;

  const tableData = useMemo(() => {
    if (!selectedBrandOutlets) return [];
    return Object.entries(selectedBrandOutlets).map(([brandId, outlets]) => {
      const isAllOutlets = Array.isArray(outlets) && outlets.includes("ALL");
      const brand = brands.find((b) => String(b.brandId) === String(brandId));
      let brandName = brand ? brand.brandName : createUserModal.sectionTitles.brandAccess.fallbackBrandName(brandId);
      if (brandId === "ALL") {
        brandName = createUserModal.sectionTitles.brandAccess.allBrandsLabel;
      }
      return {
        id: brandId,
        brandName,
        outletsCount: isAllOutlets ? createUserModal.sectionTitles.brandAccess.allOutletsLabel : (Array.isArray(outlets) ? outlets.length : 0),
      };
    });
  }, [selectedBrandOutlets, brands]);

  const columns = useMemo(() => [
    {
      header: createUserModal.sectionTitles.brandAccess.tableHeaders.brandName,
      accessor: (row) => row.brandName || '',
      width: '60%',
    },
    {
      header: createUserModal.sectionTitles.brandAccess.tableHeaders.outletsSelected,
      accessor: (row) => row.outletsCount,
      width: '40%',
    },
  ], []);

  return (
    <div className="inputGrid">
      <div className="row">
        <div className="modal-form-field">
          <div className="modal-section-header">
            <span className="modal-section-row-title">{createUserModal.sectionTitles.brandAccess.inner}</span>
            <LtButton
              label={buttonLabel}
              onClick={handleOpenModal}
              size="small"
              leftElement={
                (mode === 'edit' && hasSelected) ?
                  <LtIcon
                    useReactIcon
                    reactIcon={userManagerConstants.icons.editIcon}
                    color='#00cd7c'
                    size='mini'
                  />
                  : <span className="plusIcon">+</span>
              }
              customStyles={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                color: '#00cd7c',
                padding: 0,
                gap: '6px',
              }}
            />
          </div>

          <div className="modal-section-body-margin">
            {(!selectedBrandOutlets || Object.keys(selectedBrandOutlets).length === 0) ? (
              <p className="modal-section-placeholder-text">{createUserModal.sectionTitles.brandAccess.emptySelection}</p>
            ) : (
              <div className="table-wrapper">
                <ExpandableTable
                  tableData={tableData}
                  columns={columns}
                  expandable={false}
                  maxHeight="400px"
                  paginationEnabled={false}
                  tableHeaderStyles={{
                    background: 'white',
                  }}
                  tableHeaderCellStyles={{
                    color: '#6b7280',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                  tableBodyCellStyles={{
                    padding: '12px 16px',
                  }}
                  emptyMessage={createUserModal.sectionTitles.brandAccess.emptyTableMessage}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <LtModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        header={createUserModal.sectionTitles.brandAccess.button}
        className="brand-access-modal"
        width="800px"
      >
        <div className="multiselector-modal-wrapper">
          {(!brands || brands.length === 0 || loading) ? (
            <div className="loading-wrapper">
              <LtLoader
                size="large"
                inline="centered"
                message={createUserModal.sectionTitles.brandAccess.loadingMessage}
              />
            </div>
          ) : (
            <DynamicMultiselector
              isMultiSelector={true}
              showCloseButton={false}
              showSaveButton={true}
              loading={loading}
              data={{
                dataSchema: {
                  childId: 'outletId',
                  childName: 'outletName',
                  childrenElement: 'outletData',
                  parentId: 'brandId',
                  parentName: 'brandName',
                  subParentElement: 'childCategories',
                  allowEmptyParents: true,
                },
                filterDataList: brands,
                searchChildPlaceholder: createUserModal.sectionTitles.brandAccess.searchChildPlaceholder,
                searchPlaceholder: createUserModal.sectionTitles.brandAccess.searchPlaceholder,
                childNoDataMessage: createUserModal.sectionTitles.brandAccess.childNoDataMessage,
                selectedIds: selectedOutletIds,
                showChildSearchInput: true,
                title: createUserModal.sectionTitles.brandAccess.button,
                viewOnly: false
              }}
              onSelectionChange={() => { }}
              filteredDataList={brands}
              handleSearch={() => { }}
              onClose={() => setIsModalOpen(false)}
              onGroupChange={() => { }}
              onSave={handleSaveSelection}
              updateMultiSelectorDataInStore={() => { }}
            />
          )}
        </div>
      </LtModal>

      <IndicatorModal
        open={isErrorModalOpen}
        title="Brand Access Required"
        message="At least one brand (at least one outlet) access must be assigned to the user."
        variant="warning"
        onClose={() => setIsErrorModalOpen(false)}
        actions={[
          {
            label: "Ok",
            onClick: () => setIsErrorModalOpen(false),
            backgroundColor: '#00cd7c',
            color: '#ffffff',
          },
        ]}
        actionsAlignment="center"
        maxWidth="420px"
      />
    </div>
  );
}

export default UserBrandAccessSection;
